// BM-H82 P1-a - ClientWorkbook page.
//
// Tokenised client-facing workbook for the EPR (Estate Planning
// Review). Twenty-three questions across six thematic sections. Each
// question answer payload is {answer, confidence, client_note}. Nine
// questions accept an evidence upload. Six questions (q3, q9, q10,
// q11, q21, q22) are flagged sensitive; the client can still answer,
// but only solicitor-authenticated reads surface the answer text via
// the backend gate.
//
// Behaviour:
//  * autosave-on-blur PATCHes to /api/epr/workbook/{fid}
//  * save-and-resume: leaving and returning under the same token
//    reopens at the last section touched
//  * review-before-submit modal that shows every answer redacted
//    where sensitive
//  * final submit locks the workbook and renders the submitted lock
//    screen with the submitted timestamp on return
//
// Accessibility: basic WCAG AA. All controls have visible labels,
// keyboard tab order matches visual order, aria-live on the save
// status pill, aria-required on required fields.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, Save, ShieldCheck, UploadCloud } from "lucide-react";
import FormShell from "../components/client-forms/FormShell";
import TokenGuard from "../components/client-forms/TokenGuard";
import FieldTooltip from "../components/client-forms/FieldTooltip";
import {
  QUESTIONS,
  SECTIONS,
  WORKBOOK_SCHEMA_VERSION,
  isSensitive,
  questionsForSection,
  requiresEvidence,
} from "../lib/eprWorkbookSchema";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// BM-H3691: what the server takes (backend/hub/epr/evidence_uploads.py is the authority; a test pins these to it).
export const EVIDENCE_ACCEPT = ".doc,.docx,.jpeg,.jpg,.pdf,.png";
export const EVIDENCE_MAX_MB = 20;
export const EVIDENCE_MAX_PER_QUESTION = 5;
const CONFIDENCE_OPTIONS = [
  { value: "high", label: "Very sure" },
  { value: "medium", label: "Fairly sure" },
  { value: "low", label: "Not sure" },
  { value: "unsure", label: "Prefer not to answer yet" },
];

function nowStamp() {
  return new Date().toISOString();
}

function labelStyle() {
  return { fontSize: 14, color: "#0f172a", fontWeight: 600, marginBottom: 6 };
}

function inputStyle() {
  return {
    width: "100%",
    padding: 10,
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    fontFamily: "inherit",
    fontSize: 14,
    boxSizing: "border-box",
  };
}

function SensitiveBadge() {
  return (
    <span
      data-testid="sensitive-badge"
      title="Only your solicitor can see the answer to this question."
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: 8,
        padding: "2px 8px",
        background: "#f1f5f9",
        color: "#0f172a",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <ShieldCheck size={12} /> Sensitive
    </span>
  );
}

function sizeText(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// BM-H3691: attach a document to an evidence question, or say you will email it instead.
function EvidencePanel({ question, uploads, forwarded, status, onUpload, onForward, careEmail, matterRef }) {
  const busy = Boolean(status && status.busy);
  return (
    <div
      data-testid={`workbook-evidence-${question.id}`}
      style={{ marginTop: 12, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6 }}
    >
      <div style={labelStyle()}>Documents for this question (optional)</div>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: "#475569", margin: "0 0 8px" }}>
        If you have a copy, attach it here: a PDF, a Word document or a photo, up to {EVIDENCE_MAX_MB} MB each and up to {EVIDENCE_MAX_PER_QUESTION} files.
        Your solicitor will see it. A file cannot be removed from here once it is attached, so tell your solicitor if you attach the wrong one.
      </p>
      {uploads.length > 0 && (
        <ul data-testid={`workbook-uploads-${question.id}`} style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 13, color: "#0f172a" }}>
          {uploads.map((u) => (
            <li key={u.upload_id} data-testid={`workbook-upload-${u.upload_id}`}>{u.filename} ({sizeText(u.size_bytes || 0)})</li>
          ))}
        </ul>
      )}
      <input
        type="file"
        accept={EVIDENCE_ACCEPT}
        data-testid={`workbook-file-${question.id}`}
        aria-label={`Attach a document to ${question.id.toUpperCase()}`}
        disabled={busy || uploads.length >= EVIDENCE_MAX_PER_QUESTION}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          if (f) onUpload(f);
        }}
        style={{ fontSize: 13 }}
      />
      {busy && <div data-testid={`workbook-upload-busy-${question.id}`} aria-live="polite" style={{ fontSize: 13, color: "#475569", marginTop: 6 }}>Attaching...</div>}
      {status && status.error && (
        <div role="alert" data-testid={`workbook-upload-error-${question.id}`} style={{ fontSize: 13, color: "#991b1b", marginTop: 6 }}>{status.error}</div>
      )}
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#0f172a", marginTop: 10 }}>
        <input
          type="checkbox"
          data-testid={`workbook-forward-${question.id}`}
          checked={forwarded}
          onChange={(e) => onForward(e.target.checked)}
        />
        I will send this to my adviser by email instead
      </label>
      {forwarded && (
        <p data-testid={`workbook-forward-note-${question.id}`} style={{ fontSize: 13, color: "#475569", margin: "6px 0 0" }}>
          Thank you. Please email it{careEmail ? <> to <strong>{careEmail}</strong></> : " to your adviser"}
          {matterRef ? <>, quoting <strong>{matterRef}</strong></> : null}. It is not attached to this questionnaire until it arrives.
        </p>
      )}
    </div>
  );
}

function QuestionRow({ question, value, onChange, disabled, evidencePanel }) {
  const val = value || { answer: "", confidence: "", client_note: "" };
  // BM-H3676: for the client the server hides sensitive answers it already
  // holds (redacted: blank, flagged). A blank box on return is NOT an empty
  // answer. Nothing is saved from it until the client types a new answer.
  const redacted = Boolean(val._redacted);
  const sensitive = isSensitive(question.id);
  const evidence = requiresEvidence(question.id);
  return (
    <fieldset
      data-testid={`workbook-question-${question.id}`}
      style={{
        border: "1px solid #e2e8f0",
        borderLeft: sensitive ? "4px solid #0f766e" : "4px solid #cbd5e1",
        borderRadius: 8,
        padding: 16,
        marginBottom: 14,
        background: "#fff",
      }}
      disabled={disabled}
    >
      <legend style={{ padding: "0 6px", fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>
        {question.id.toUpperCase()}
      </legend>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontSize: 15, lineHeight: 1.5, color: "#0f172a", fontWeight: 600 }}>
          {question.prompt}
          {sensitive && <SensitiveBadge />}
          <FieldTooltip
            testid={`workbook-tooltip-${question.id}`}
            text={
              sensitive
                ? "Please answer honestly. Only your solicitor can view your answer. Support staff cannot."
                : "Take your time. You can save and return."
            }
          />
        </div>
        {evidence && (
          <span
            data-testid={`workbook-evidence-hint-${question.id}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              color: "#0f766e",
              fontWeight: 600,
            }}
          >
            <UploadCloud size={12} /> Evidence may be requested
          </span>
        )}
      </div>

      {question.help && (
        // BM-H3971: the solicitor's help text for the lifetime transfer questions.
        <p
          id={`${question.id}-help`}
          data-testid={`workbook-help-${question.id}`}
          style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: "#475569" }}
        >
          {question.help}
        </p>
      )}

      <div style={{ marginTop: 12 }}>
        <label htmlFor={`${question.id}-answer`} style={labelStyle()}>Your answer</label>
        <textarea
          id={`${question.id}-answer`}
          aria-describedby={question.help ? `${question.id}-help` : undefined}
          data-testid={`workbook-answer-${question.id}`}
          value={val.answer || ""}
          onChange={(e) => onChange({ ...val, _redacted: undefined, answer: e.target.value })}
          onBlur={() => {
            if (redacted) return;
            onChange({ ...val, answer: (val.answer || "").trim() }, { flush: true });
          }}
          placeholder={redacted ? "Your answer is saved. Only your solicitor can see it. Type here to replace it." : undefined}
          rows={3}
          aria-required={!sensitive ? "false" : "false"}
          style={{ ...inputStyle(), resize: "vertical", minHeight: 72 }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div>
          <label htmlFor={`${question.id}-confidence`} style={labelStyle()}>How sure are you?</label>
          <select
            id={`${question.id}-confidence`}
            data-testid={`workbook-confidence-${question.id}`}
            value={val.confidence || ""}
            disabled={redacted}
            onChange={(e) => onChange({ ...val, confidence: e.target.value }, { flush: true })}
            style={inputStyle()}
          >
            <option value="">Select</option>
            {CONFIDENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${question.id}-note`} style={labelStyle()}>Anything you want to add?</label>
          <input
            id={`${question.id}-note`}
            data-testid={`workbook-note-${question.id}`}
            type="text"
            value={val.client_note || ""}
            disabled={redacted}
            onChange={(e) => onChange({ ...val, client_note: e.target.value })}
            onBlur={() => {
              if (redacted) return;
              onChange({ ...val, client_note: (val.client_note || "").trim() }, { flush: true });
            }}
            style={inputStyle()}
            placeholder="Optional note for your solicitor"
          />
        </div>
      </div>
      {evidence && evidencePanel}
    </fieldset>
  );
}

function ReviewSummary({ answers, onEditSection, onConfirm, onCancel, isSubmitting }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="workbook-review-modal"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div style={{ background: "#fff", borderRadius: 10, maxWidth: 780, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24 }}>
        <h2 style={{ marginTop: 0, fontSize: 22, color: "#0f172a" }}>Please review before you submit</h2>
        <p style={{ fontSize: 14, color: "#334155", marginBottom: 16 }}>
          Once submitted, this record is locked. Corrections after this point go through your solicitor and are reviewed by the supervising principal (VC) before they take effect.
        </p>
        {SECTIONS.map((s) => (
          <div key={s.id} data-testid={`review-section-${s.id}`} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{s.title}</div>
              <button type="button" data-testid={`review-edit-${s.id}`} onClick={() => onEditSection(s.id)} style={{ background: "transparent", border: "none", color: "#0f766e", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                Edit
              </button>
            </div>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18 }}>
              {questionsForSection(s.id).map((q) => {
                const v = answers[q.id] || {};
                const sensitive = isSensitive(q.id);
                return (
                  <li key={q.id} data-testid={`review-q-${q.id}`} style={{ marginBottom: 6, fontSize: 13, color: "#334155" }}>
                    <strong>{q.id.toUpperCase()}: </strong>
                    {sensitive ? (
                      <em>Answer stored securely; only your solicitor can view.</em>
                    ) : (
                      <span>{v.answer ? v.answer : <em style={{ color: "#94a3b8" }}>No answer yet</em>}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 20 }}>
          <button type="button" data-testid="workbook-cancel-btn" onClick={onCancel} style={{ padding: "8px 14px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 6, color: "#334155", cursor: "pointer", fontWeight: 600 }}>
            Back to editing
          </button>
          <button
            type="button"
            data-testid="workbook-final-submit-btn"
            onClick={onConfirm}
            disabled={isSubmitting}
            style={{ padding: "8px 14px", background: "#0f766e", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, opacity: isSubmitting ? 0.6 : 1 }}
          >
            {isSubmitting ? "Submitting..." : "Submit workbook"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubmittedLockScreen({ submittedAt }) {
  const nice = submittedAt ? new Date(submittedAt).toLocaleString("en-GB") : "recently";
  return (
    <div
      data-testid="workbook-submitted-lock"
      style={{
        maxWidth: 640,
        margin: "80px auto",
        padding: 32,
        fontFamily: "Georgia, serif",
        color: "#111",
        background: "#fff",
        borderRadius: 10,
        border: "1px solid #e2e8f0",
        textAlign: "center",
      }}
    >
      <CheckCircle size={48} color="#0f766e" style={{ margin: "0 auto 12px" }} />
      <h1 style={{ fontSize: 24, marginTop: 0 }}>Thank you, your workbook is with us</h1>
      <p style={{ fontSize: 15, lineHeight: 1.6, color: "#334155" }}>
        You submitted your Estate Planning Review workbook on <strong>{nice}</strong>. Your solicitor now has everything they need to prepare for your appointment.
      </p>
      <p style={{ fontSize: 14, lineHeight: 1.6, color: "#475569" }}>
        If you spot something that needs correcting after submission, contact your solicitor. Corrections after this point are reviewed by our supervising principal (VC) before any amended material issues to you.
      </p>
    </div>
  );
}

function InnerWorkbook({ token, formId }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState("s1");

  const headers = useMemo(() => ({ "Content-Type": "application/json", "X-Form-Token": token }), [token]);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/epr/workbook/${formId}`, { headers });
      if (!resp.ok) throw new Error(`load failed: ${resp.status}`);
      const data = await resp.json();
      setForm(data);
    } catch (e) {
      setError(e.message || "Unable to load your workbook");
    }
  }, [formId, headers]);

  useEffect(() => { load(); }, [load]);

  // BM-H3676: returns true only when the server confirms it kept every answer
  // sent. The page used to say nothing of the outcome beyond a small pill and
  // carried on, so an answer the server refused still appeared on the review
  // screen and was left out of the submission.
  const patchAnswers = useCallback(async (partial) => {
    if (!form || form.state === "submitted") return true;
    // "" is not a confidence level; the server treats null as "not chosen".
    // A redacted placeholder (hidden stored answer) is never sent back.
    const answers = {};
    for (const [qid, v] of Object.entries(partial)) {
      if (!v || v._redacted) continue;
      answers[qid] = {
        answer: v.answer ?? null,
        confidence: v.confidence ? v.confidence : null,
        client_note: v.client_note ?? null,
      };
    }
    const sent = Object.keys(answers);
    if (!sent.length) return true;
    setSaveStatus("saving");
    try {
      const resp = await fetch(`${API_BASE}/api/epr/workbook/${formId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ answers }),
      });
      if (!resp.ok) throw new Error(`save failed: ${resp.status}`);
      const out = await resp.json();
      const kept = new Set(out && Array.isArray(out.saved_qs) ? out.saved_qs : []);
      if (!sent.every((q) => kept.has(q))) throw new Error("the server did not keep every answer");
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 1200);
      return true;
    } catch {
      setSaveStatus("error");
      return false;
    }
  }, [form, formId, headers]);

  // BM-H3691. Only the uploads and the email choices are refreshed from the server: the answers on the page may
  // hold something the client has typed and not yet saved, and must not be overwritten.
  const [evidenceStatus, setEvidenceStatus] = useState({});
  const refreshEvidence = useCallback(async () => {
    const resp = await fetch(`${API_BASE}/api/epr/workbook/${formId}`, { headers });
    if (!resp.ok) throw new Error(`load failed: ${resp.status}`);
    const data = await resp.json();
    setForm((prev) => (prev ? { ...prev, uploads: data.uploads || [], evidence_forwarding: data.evidence_forwarding || {} } : prev));
  }, [formId, headers]);

  const uploadEvidence = useCallback(async (qid, file) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!EVIDENCE_ACCEPT.split(",").includes(ext)) {
      setEvidenceStatus((s) => ({ ...s, [qid]: { busy: false, error: "We can only take a PDF, a Word document or a photo (JPEG or PNG). Tick the box to send it to your adviser by email instead." } }));
      return;
    }
    if (file.size > EVIDENCE_MAX_MB * 1024 * 1024) {
      setEvidenceStatus((s) => ({ ...s, [qid]: { busy: false, error: `That file is too large. The limit is ${EVIDENCE_MAX_MB} MB for each file.` } }));
      return;
    }
    setEvidenceStatus((s) => ({ ...s, [qid]: { busy: true, error: null } }));
    try {
      const body = new FormData();
      body.append("question_id", qid);
      body.append("file", file);
      const resp = await fetch(`${API_BASE}/api/epr/workbook/${formId}/upload`, { method: "POST", headers: { "X-Form-Token": token }, body });
      if (!resp.ok) {
        let message = "We could not attach that file. Please try again, or tick the box to send it to your adviser by email instead.";
        try {
          const j = await resp.json();
          if (j && j.detail && j.detail.message) message = j.detail.message;
        } catch { /* keep the plain message above */ }
        throw Object.assign(new Error(message), { plain: true });
      }
      await refreshEvidence();
      setEvidenceStatus((s) => ({ ...s, [qid]: { busy: false, error: null } }));
    } catch (e) {
      // Only a message written for the client is shown. A dropped connection or a browser error is not.
      const shown = e && e.plain ? e.message : "We could not attach that file. Please try again, or tick the box to send it to your adviser by email instead.";
      setEvidenceStatus((s) => ({ ...s, [qid]: { busy: false, error: shown } }));
    }
  }, [formId, token, refreshEvidence]);

  const setForwarding = useCallback(async (qid, checked) => {
    setEvidenceStatus((s) => ({ ...s, [qid]: { busy: false, error: null } }));
    try {
      const resp = await fetch(`${API_BASE}/api/epr/workbook/${formId}/evidence-forwarding`, {
        method: "PUT", headers, body: JSON.stringify({ question_id: qid, forward_by_email: checked }),
      });
      if (!resp.ok) throw new Error(`save failed: ${resp.status}`);
      const out = await resp.json();
      setForm((prev) => (prev ? { ...prev, evidence_forwarding: out.evidence_forwarding || {} } : prev));
    } catch {
      setEvidenceStatus((s) => ({ ...s, [qid]: { busy: false, error: "We could not save that choice. Please try again." } }));
    }
  }, [formId, headers]);

  const onQuestionChange = useCallback((qid, next, opts) => {
    setForm((prev) => {
      if (!prev) return prev;
      const answers = { ...(prev.answers || {}), [qid]: next };
      return { ...prev, answers, _dirty: true, _lastEdited: nowStamp() };
    });
    if (opts && opts.flush) {
      patchAnswers({ [qid]: next });
    }
  }, [patchAnswers]);

  // BM-H3676: the review screen shows what is on this page, so it is only
  // opened once the server has confirmed it holds all of it.
  const openReview = useCallback(async () => {
    setReviewError(null);
    const unsaved = {};
    for (const [qid, v] of Object.entries((form && form.answers) || {})) {
      if (!v || v._redacted) continue;
      if ((v.answer || "").trim() || v.confidence || (v.client_note || "").trim()) unsaved[qid] = v;
    }
    const ok = await patchAnswers(unsaved);
    if (!ok) {
      setReviewError(
        "We could not save your answers, so we have not opened the review. Nothing has been submitted. Please check your connection and try again."
      );
      return;
    }
    setReviewing(true);
  }, [form, patchAnswers]);

  const submitFinal = useCallback(async () => {
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/epr/workbook/${formId}/submit`, {
        method: "POST",
        headers,
        body: JSON.stringify({ confirm: true }),
      });
      if (!resp.ok) throw new Error(`submit failed: ${resp.status}`);
      const data = await resp.json();
      await load();
      setReviewing(false);
      return data;
    } catch (e) {
      setError(e.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }, [formId, headers, load]);

  if (error) {
    return (
      <div data-testid="workbook-error" style={{ maxWidth: 640, margin: "80px auto", padding: 24, fontFamily: "Georgia, serif" }}>
        <p>Something went wrong: {error}</p>
        <button type="button" onClick={() => window.location.reload()} data-testid="workbook-retry-btn">Retry</button>
      </div>
    );
  }
  if (!form) {
    return <div data-testid="workbook-loading" style={{ padding: 40, textAlign: "center" }}>Loading your workbook...</div>;
  }

  if (form.state === "submitted") {
    return <SubmittedLockScreen submittedAt={form.submitted_at} />;
  }

  const sections = SECTIONS.map((s) => ({ id: s.id, title: s.title }));
  const rightRail = (
    <aside
      data-testid="workbook-right-rail"
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: 16,
        fontFamily: "Georgia, serif",
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
        Autosave
      </div>
      <div
        aria-live="polite"
        data-testid="workbook-save-status"
        style={{ fontSize: 13, fontWeight: 600, color: saveStatus === "error" ? "#b91c1c" : "#0f172a" }}
      >
        <Save size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />
        {saveStatus === "saving" && "Saving..."}
        {saveStatus === "saved" && "Saved just now"}
        {saveStatus === "error" && "Couldn't save. Trying again on next answer."}
        {saveStatus === "idle" && "Your answers save automatically as you go."}
      </div>
      <div style={{ borderTop: "1px solid #e2e8f0", marginTop: 12, paddingTop: 12, fontSize: 12, color: "#475569", lineHeight: 1.5 }}>
        Workbook schema {WORKBOOK_SCHEMA_VERSION}.
        You can close this page and reopen the link at any time within its 30-day window.
      </div>
    </aside>
  );

  return (
    <>
      <FormShell
        matterRef={form.matter_ref}
        clientName={form.client_name || null}
        sections={sections}
        activeSection={activeSection}
        onSaveAndReturn={() => window.close()}
        onRequestCallback={() => {
          // The address comes from the server with the workbook. If it is ever
          // missing, say so rather than leave a button that does nothing.
          if (form.client_care_email) {
            window.open(`mailto:${form.client_care_email}?subject=Callback%20request`, "_self");
          } else {
            setReviewError("We could not open your email. Please reply to the email we sent you with this link and ask for a call.");
          }
        }}
        rightRail={rightRail}
        questionnaireLabel="Estate Planning Review"
        documentNoun="estate planning review"
      >
        <div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: "#334155", marginBottom: 20 }}>
            Please answer as best you can. Anything you are unsure about, mark <em>Not sure</em> and add a note. Your solicitor will pick these up in the appointment.
          </p>

          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`workbook-section-${s.id}`} aria-labelledby={`heading-${s.id}`}>
              <h2 id={`heading-${s.id}`} style={{ fontSize: 20, color: "#0f172a", margin: "16px 0" }}
                  onFocus={() => setActiveSection(s.id)}>
                {s.title}
              </h2>
              {questionsForSection(s.id).map((q) => (
                <QuestionRow
                  key={q.id}
                  question={q}
                  value={(form.answers || {})[q.id]}
                  onChange={(next, opts) => onQuestionChange(q.id, next, opts)}
                  disabled={form.state === "submitted"}
                  evidencePanel={requiresEvidence(q.id) ? (
                    <EvidencePanel
                      question={q}
                      uploads={(form.uploads || []).filter((u) => u.question_id === q.id)}
                      forwarded={Boolean(((form.evidence_forwarding || {})[q.id] || {}).forward_by_email)}
                      status={evidenceStatus[q.id]}
                      onUpload={(file) => uploadEvidence(q.id, file)}
                      onForward={(checked) => setForwarding(q.id, checked)}
                      careEmail={form.client_care_email}
                      matterRef={form.matter_ref}
                    />
                  ) : null}
                />
              ))}
            </section>
          ))}

          {reviewError && (
            <div
              role="alert"
              data-testid="workbook-review-error"
              style={{ marginTop: 16, padding: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, color: "#991b1b", fontSize: 14 }}
            >
              {reviewError}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
            <button
              type="button"
              data-testid="workbook-review-btn"
              onClick={openReview}
              style={{
                padding: "12px 20px",
                background: "#0f766e",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              Review and submit
            </button>
          </div>
        </div>
      </FormShell>
      {reviewing && (
        <ReviewSummary
          answers={form.answers || {}}
          onEditSection={(sid) => { setActiveSection(sid); setReviewing(false); }}
          onConfirm={submitFinal}
          onCancel={() => setReviewing(false)}
          isSubmitting={submitting}
        />
      )}
    </>
  );
}

export default function ClientWorkbook() {
  const { form_instance_id: formId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  return (
    <TokenGuard token={token}>
      <InnerWorkbook token={token} formId={formId} />
    </TokenGuard>
  );
}
